import React from 'react';
import { 
  Sparkles, 
  Send, 
  Cloud, 
  BookOpen, 
  Kanban,
  Zap,
  Sliders,
  ShieldCheck,
  Upload,
  FileCheck2
} from 'lucide-react';

interface NavbarProps {
  activeTab: 'simple_start' | 'dossier' | 'cv_tailor' | 'tracker' | 'outreach' | 'playbook';
  setActiveTab: (tab: 'simple_start' | 'dossier' | 'cv_tailor' | 'tracker' | 'outreach' | 'playbook') => void;
  onOpenDeployGuide: () => void;
  onOpenUploadCv: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  activeTab, 
  setActiveTab, 
  onOpenDeployGuide,
  onOpenUploadCv
}) => {
  return (
    <header className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Brand Logo & Title */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActiveTab('simple_start')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-400 via-teal-500 to-sky-500 p-0.5 shadow-lg shadow-emerald-500/20 flex items-center justify-center">
              <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-bold text-lg tracking-tight text-white">Cyber<span className="text-emerald-400">Hunt</span> & MSP</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  InfoSec Dossier
                </span>
              </div>
              <p className="text-[11px] text-slate-400 hidden sm:block">Personal Cybersecurity & MSP Command Center</p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto py-1">
            <button
              onClick={() => setActiveTab('simple_start')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                activeTab === 'simple_start'
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/25'
                  : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              <Zap className="w-4 h-4 text-amber-300" />
              <span>3-Step Quick Start</span>
            </button>

            <button
              onClick={() => setActiveTab('dossier')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'dossier'
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/25'
                  : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              <FileCheck2 className="w-4 h-4 text-emerald-300" />
              <span>My Security Dossier</span>
            </button>

            <button
              onClick={() => setActiveTab('tracker')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'tracker'
                  ? 'bg-sky-500 text-white shadow-md shadow-sky-500/25'
                  : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              <Kanban className="w-4 h-4" />
              <span className="hidden sm:inline">My Applications</span>
              <span className="sm:hidden">Tracker</span>
            </button>

            <button
              onClick={() => setActiveTab('outreach')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'outreach'
                  ? 'bg-sky-500 text-white shadow-md shadow-sky-500/25'
                  : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">MSP & Cyber Pitch</span>
              <span className="sm:hidden">Pitch</span>
            </button>

            <button
              onClick={() => setActiveTab('playbook')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'playbook'
                  ? 'bg-sky-500 text-white shadow-md shadow-sky-500/25'
                  : 'text-slate-300 hover:text-white hover:bg-slate-900'
              }`}
            >
              <BookOpen className="w-4 h-4 text-amber-400" />
              <span className="hidden md:inline">Where to Find Contracts</span>
              <span className="md:hidden">Places</span>
            </button>
          </nav>

          {/* CV Upload Button & Cloudflare Badge */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onOpenUploadCv}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-md shadow-emerald-500/20 transition-all"
              title="Upload Your Cybersecurity CV"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Upload My CV</span>
            </button>

            <button
              onClick={onOpenDeployGuide}
              className="hidden sm:flex items-center space-x-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-900 hover:bg-slate-800 text-orange-400 border border-orange-500/30 transition-all"
              title="Cloudflare Free Setup"
            >
              <Cloud className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[10px]">£0/mo</span>
            </button>
          </div>

        </div>
      </div>
    </header>
  );
};
